import { EventEmitter, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectAuthLoading, selectLanguaje, selectStoreLoading } from '@store/selectors';
import { ComponentEnum, LanguajeEnum } from '@core/enums';
import { I18nService } from '@core/services';
import { IAbstractPresenter, ILanguageObject } from '@core/interfaces';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ILoginStrategyComponent, ILoginStrategyPresenter } from './interfaces';
import { AuthModalComponentEnum } from '../../../enums';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AUTH_LOG_IN, AUTH_UPDATE_FORM } from '@store/actions';
import { IAuthModalCallback } from '../../../interfaces';

@Injectable({
	providedIn: 'root',
})
export class LoginStrategyPresenter
	extends IAbstractPresenter<ILoginStrategyComponent>
	implements ILoginStrategyPresenter
{
	loading$: Observable<boolean>;
	loadingStore$: Observable<boolean>;
	languageEnum$: Observable<LanguajeEnum>;
	translations: ILanguageObject = {};
	formGroup: FormGroup;
	AuthModalComponentEnum = AuthModalComponentEnum;
	visitingCheckout: boolean = false;
	callback!: IAuthModalCallback;

	guest: EventEmitter<void> = new EventEmitter<void>();

	constructor(
		private store: Store,
		private i18nService: I18nService,
		private formBuilder: FormBuilder
	) {
		super();

		this.loading$ = this.store.select(selectAuthLoading);
		this.loadingStore$ = this.store.select(selectStoreLoading);
		this.languageEnum$ = this.store.select(selectLanguaje);

		this.formGroup = this.formBuilder.group({
			email: [
				null,
				[
					Validators.required,
					Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/),
				],
			],
			password: [null, [Validators.required]],
		});
	}

	override onInit(): void {
		this.destroy$ = new Subject<void>();
		this.formGroup.reset();
	}

	dispatchActions(): void {}

	suscribeObservers(): void {
		this.languageEnum$.pipe(takeUntil(this.destroy$)).subscribe(lang => {
			const trans = this.i18nService.getTranslations(ComponentEnum.Auth, lang);
			this.translations = trans;
			this.view.translations = trans;
		});

		this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
			// Dispatch only login fields but respect the whole form state expected by the store
			this.store.dispatch(AUTH_UPDATE_FORM({ authForm: { ...value } }));
		});
	}

	onLogIn(): void {
		if (this.formGroup.valid) {
			this.store.dispatch(AUTH_LOG_IN());
		}
	}

	isFieldInvalid(fieldName: string): boolean {
		const control = this.formGroup.get(fieldName);
		return !!control?.errors && !!(control?.dirty || control?.touched);
	}

	getFieldErrorMessage(fieldName: string): string {
		const control = this.formGroup.get(fieldName);
		if (!control?.errors) return '';

		if (fieldName === 'email') {
			if (control.errors['required']) return this.translations?.['emailRequired'] || 'Email is required';
			if (control.errors['email'] || control.errors['pattern'])
				return this.translations?.['emailInvalid'] || 'Invalid email format';
		}
		if (fieldName === 'password') {
			if (control.errors['required']) return this.translations?.['passwordRequired'] || 'Password is required';
		}
		return '';
	}

	navigateTo(route: AuthModalComponentEnum): void {
		this.view.callback?.navigate(route);
	}

	navigateGuest(): void {
		this.view.callback?.navigateGuest();
	}
}
